interface TextSegment {
  text: string;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isBreak?: boolean;
}

/**
 * Parses text with HTML-like formatting tags into text segments with format information
 * @param inputText Text with formatting tags like <b>, <i>, <u>, <br> and HTML entities like &nbsp;
 * @returns Array of text segments with formatting info and line break indicators
 */
export const parseFormattedText = (inputText: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let remainingText = inputText;

  // Find all opening and closing tags, and <br> tags
  const tagRegex = /<\/?[biu]>|<br>/g;
  let activeFormats = { bold: false, italic: false, underline: false };
  let match;

  while (remainingText.length > 0) {
    match = tagRegex.exec(remainingText);

    if (!match) {
      // No more tags, add the remaining text
      if (remainingText.length > 0) {
        segments.push({
          text: parseSpecialChars(remainingText),
          isBold: activeFormats.bold,
          isItalic: activeFormats.italic,
          isUnderline: activeFormats.underline,
          isBreak: false
        });
      }
      break;
    }

    // Add text before the tag
    if (match.index > 0) {
      const textBeforeTag = remainingText.substring(0, match.index);
      segments.push({
        text: parseSpecialChars(textBeforeTag),
        isBold: activeFormats.bold,
        isItalic: activeFormats.italic,
        isUnderline: activeFormats.underline,
        isBreak: false
      });
    }

    // Update active formats based on the tag
    const tag = match[0];
    if (tag === '<b>') activeFormats.bold = true;
    else if (tag === '</b>') activeFormats.bold = false;
    else if (tag === '<i>') activeFormats.italic = true;
    else if (tag === '</i>') activeFormats.italic = false;
    else if (tag === '<u>') activeFormats.underline = true;
    else if (tag === '</u>') activeFormats.underline = false;
    else if (tag === '<br>') {
      // Add a line break segment
      segments.push({
        text: '',
        isBold: activeFormats.bold,
        isItalic: activeFormats.italic,
        isUnderline: activeFormats.underline,
        isBreak: true
      });
    }

    // Move past this tag for next iteration
    remainingText = remainingText.substring(match.index + tag.length);
    tagRegex.lastIndex = 0; // Reset regex
  }

  return segments;
};


/**
 * Converts HTML special characters references like &nbsp; to their actual characters
 * @param inputText Text with HTML entity references
 * @returns Text with entity references converted to actual characters
 */
export const parseSpecialChars = (inputText: string): string => {
  if (!inputText) return '';
  
  // Create a temporary element to handle the HTML entity decoding
  const tempElement = document.createElement('div');
  
  // Replace common HTML entities directly for better performance
  const processedText = inputText
    .replace(/&nbsp;/g, ' ')    // Non-breaking space
    .replace(/&amp;/g, '&')     // Ampersand
    .replace(/&lt;/g, '<')      // Less than
    .replace(/&gt;/g, '>')      // Greater than
    .replace(/&quot;/g, '"')    // Double quote
    .replace(/&apos;/g, "'")    // Single quote (apostrophe)
    .replace(/&laquo;/g, '«')   // Left-pointing double angle quotation mark
    .replace(/&raquo;/g, '»')   // Right-pointing double angle quotation mark
    .replace(/&ndash;/g, '–')   // En dash
    .replace(/&mdash;/g, '—')   // Em dash
    .replace(/&hellip;/g, '…'); // Horizontal ellipsis
    
  // For any other HTML entities that weren't directly replaced
  tempElement.innerHTML = processedText;
  return tempElement.textContent || '';
}
/**
 * Converts plain text with <b>, <i>, <u> tags to semantic HTML
 * @param inputText Text with formatting tags
 * @returns HTML string with proper semantic elements
 */
export const formattedTextToHtml = (inputText: string): string => {
  if (!inputText) return '';
  
  // First decode HTML entities
  const decodedText = parseSpecialChars(inputText);
  
  // Replace formatting tags with semantic HTML
  return decodedText
    .replace(/<b>(.*?)<\/b>/g, '<strong>$1</strong>')
    .replace(/<i>(.*?)<\/i>/g, '<em>$1</em>')
    .replace(/<u>(.*?)<\/u>/g, '<span class="underline">$1</span>')
    .replace(/<br>/g, '<br />');
};

/**
 * Converts plain text with <b>, <i>, <u> tags to accessible aria attributes
 * @param inputText Text with formatting tags
 * @returns Plain text with extra spaces for screen readers to pause
 */
export const formattedTextToAccessible = (inputText: string): string => {
  if (!inputText) return '';
  
  // First decode HTML entities
  const decodedText = parseSpecialChars(inputText);
  
  // Remove HTML tags and add slight pauses for emphasis where tags were
  return decodedText
    .replace(/<b>(.*?)<\/b>/g, ' $1 ') // Add slight pauses around emphasized text
    .replace(/<i>(.*?)<\/i>/g, ' $1 ')
    .replace(/<u>(.*?)<\/u>/g, ' $1 ')
    .replace(/<br>/g, '\n')           // Convert br tags to newlines
    .replace(/\s+/g, ' ')             // Normalize spaces
    .trim();                          // Trim extra spaces
};

// Export the TextSegment interface
export type { TextSegment };
